import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
  ClickAwayListener,
  Portal,
  Tooltip,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import DeleteIcon from "@mui/icons-material/Delete";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EditDiscountModal from "./EditDiscountModal";
import EditUserModal from "./EditUserModal";
import TagManagerModal from "./TagManagerModal";
import AccessHoldModal from "./AccessHoldModal";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatPersonName } from "../../../utils/formatPersonName";
import {
  FUNIL_ETAPA_ENCERRADA,
  normalizarEtapaVendasLegada,
} from "../../../utils/vendasFunnel.js";
import { openAdminWhatsApp } from "../../../utils/adminWhatsApp";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { registrarSalesEvent } from "../../../utils/salesEvents.js";

function formatShortAccessDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "hoje";
  if (diffDays === 1) return "ontem";
  if (diffDays < 7) return `${diffDays}d atrás`;

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });
}

const UserRow = ({
  user,
  onDelete,
  onUpdateDiscount,
  onUpdateTags,
  onRenewalNotified,
  onUpdateUser,
  activeMenuId,
  setActiveMenu,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

const statusNormalizado = String(user?.status || "")
  .trim()
  .toLowerCase();

const formatCurrencyBRL = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const formatPhoneBR = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "N/A";

  const local = digits.length > 11 ? digits.slice(-11) : digits;

  if (local.length === 11) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }

  if (local.length === 10) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }

  return value;
};

const formatDateTimeBR = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour12: false,
  });
};

const getDiscountOfferText = (userData) => {
  const type = String(userData?.discount_type || "").toLowerCase();

  if (type.includes("199")) return "R$199";
  if (type.includes("179")) return "R$179";
  if (type.includes("149")) return "R$149";

  if (type.includes("valor_livre")) {
    return formatCurrencyBRL(userData?.custom_value);
  }

  return null;
};

const getRadialMenuLayout = (totalIcons) => {
  if (totalIcons <= 3) {
    return { angles: [145, 195, 245].slice(0, totalIcons), radius: 64 };
  }

  if (totalIcons === 4) {
    return { angles: [132, 172, 212, 252], radius: 66 };
  }

  if (totalIcons === 5) {
    return { angles: [120, 154, 188, 222, 256], radius: 69 };
  }

  if (totalIcons === 6) {
    return { angles: [110, 138, 166, 194, 222, 250], radius: 72 };
  }

  const startAngle = 105;
  const endAngle = 255;
  const step = (endAngle - startAngle) / (totalIcons - 1);

  return {
    angles: Array.from({ length: totalIcons }, (_, index) => startAngle + step * index),
    radius: 72,
  };
};

  const [expanded, setExpanded] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [showDiscountWhatsApp, setShowDiscountWhatsApp] = useState(false);
  const [blinkDiscount, setBlinkDiscount] = useState(false);
  const [blinkEmailInvalid] = useState(false);
  const [openTagModal, setOpenTagModal] = useState(false);
  const [openAccessHoldModal, setOpenAccessHoldModal] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuButtonRef = useRef(null);
  const menuOpen = activeMenuId === user?.id;

  const abrirWhatsAppAdmin = (message = "") => {
    openAdminWhatsApp(user?.phone, message);
  };

  const handleTrackedAdminWhatsApp = async (e, metadata = {}) => {
    e?.stopPropagation?.();
    if (!user?.phone) return;

    abrirWhatsAppAdmin(metadata?.message || "");
    registrarSalesEvent(user.id, "admin_whatsapp_open", {
      source: "admin_user_row",
      metadata,
    });
  };

  const handleAvancarEtapa = async (e) => {
    e.stopPropagation();

    if (!user?.acao_sugerida?.key) return;

    if (user?.phone) {
      abrirWhatsAppAdmin("");
      registrarSalesEvent(user.id, "admin_whatsapp_open", {
        source: "admin_user_row_funnel",
        metadata: {
          acao: user.acao_sugerida.key,
        },
      });
    }


    // 2️⃣ chama backend
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/users/${user.id}/vendas`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          acao: user.acao_sugerida.key,
        }),
      }
    );

    if (!response.ok) return;

    // 3️⃣ ATUALIZA NO FRONT (sem refresh)
    const data = await response.json();

    onUpdateUser({
      ...user,
      ...data,
    });
  };

  // Função para calcular a posição inicial do menu
  const updateMenuPosition = () => {
    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

      const top = rect.bottom + scrollTop - 80;
      const left = rect.left + scrollLeft + rect.width / 2 - 35;

      setMenuPosition({ top, left });
    }
  };

  // Abrir ou fechar o menu
  const handleToggleMenu = (e) => {
    e.stopPropagation();
    if (typeof setActiveMenu !== "function") {
      console.error(
        "setActiveMenu não é uma função. Verifique se a prop foi passada corretamente."
      );
      return;
    }
    if (!menuOpen) {
      updateMenuPosition();
      setActiveMenu(user?.id);
    } else {
      setActiveMenu(null);
    }
  };

  // Fechar o menu ao clicar fora
  const handleCloseMenu = () => {
    if (typeof setActiveMenu === "function") {
      setActiveMenu(null);
    }
  };

  useEffect(() => {
    const discountAvailable =
      user?.discount_notified === 0 &&
      user?.discount_type &&
      user.discount_type !== "none";

    let discountInterval;

    if (discountAvailable) {
      setShowDiscountWhatsApp(true);
      discountInterval = setInterval(() => {
        setBlinkDiscount((prev) => !prev);
      }, 500);
    } else {
      setShowDiscountWhatsApp(false);
      setBlinkDiscount(false);
    }

    return () => {
      clearInterval(discountInterval);
    };
  }, [user]);

  const handleToggleExpand = () => setExpanded(!expanded);

  const handleSendDiscountMessage = async (e) => {
    e.stopPropagation();
    if (!user?.discount_type || !user?.phone) {
      console.error("Dados incompletos para envio de mensagem");
      return;
    }

    const offerText = getDiscountOfferText(user);
    const rawMessage = offerText
      ? `Desconto aplicado para você!\nValor especial: ${offerText}.\nVocê já pode realizar a assinatura pelo nosso sistema no link abaixo:\n\nhttps://futuretrade-login.vercel.app`
      : "Desconto aplicado.\nVocê já pode realizar a assinatura pelo nosso sistema no link abaixo:\n\nhttps://futuretrade-login.vercel.app";

    try {
      openAdminWhatsApp(user.phone, rawMessage);
      registrarSalesEvent(user.id, "discount_whatsapp_open", {
        source: "admin_user_row",
        metadata: { discount_type: user.discount_type },
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/${user.id}/mark-notified`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ discount_notified: 1 }),
        }
      );

      if (response.ok) {
        if (typeof onUpdateUser === "function") {
          onUpdateUser({
            ...user,
            discount_notified: 1,
          });
        }
        setShowDiscountWhatsApp(false);
        setBlinkDiscount(false);
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
    }
  };

  const handleSendRenewalMessage = async (e) => {
    e.stopPropagation();

    if (!user?.phone) {
      console.error("Usuário sem telefone");
      return;
    }

    const firstName = formatPersonName(user?.full_name)
      ? formatPersonName(user?.full_name).split(" ")[0]
      : "";
    const rawMessage =
      `Oi ${firstName}, tudo bem? Notei que seu acesso à Future Trade venceu.\n` +
      "Tenho algumas condições de desconto para renovação aqui.\n" +
      "Tem interesse em renovar com desconto?";

    try {
      // 1️⃣ abre WhatsApp
      openAdminWhatsApp(user.phone, rawMessage);
      registrarSalesEvent(user.id, "renewal_whatsapp_open", {
        source: "admin_user_row",
      });

      // 2️⃣ marca como notificado no backend
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/${user.id}/renewal-notified`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        console.error("Erro ao marcar renovação como notificada");
        return;
      }
      if (typeof onRenewalNotified === "function") {
        onRenewalNotified(user.id);
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem de renovação:", error);
    }
  };

  const handleRefundLatestPayment = async (e) => {
    e.stopPropagation();

    if (isRefunding) return;

    const confirmed = window.confirm(
      `Estornar o pagamento mais recente pago de ${formattedFullName}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsRefunding(true);
      handleCloseMenu();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/${user?.id}/refund-latest-payment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao solicitar estorno.");
      }

      const refundStatus = data?.refund?.status
        ? `\nStatus: ${data.refund.status}`
        : "";
      const paymentId = data?.payment?.id ? `\nPagamento: ${data.payment.id}` : "";

      window.alert(`${data?.message || "Estorno solicitado."}${paymentId}${refundStatus}`);
    } catch (error) {
      window.alert(error.message || "Erro ao solicitar estorno.");
    } finally {
      setIsRefunding(false);
    }
  };

  const isExpired =
    user?.subscription_expires_at &&
    new Date(user.subscription_expires_at) <= new Date();
  const refundExecutionStatus = String(user?.access_hold_refund_status || "")
    .trim()
    .toLowerCase();
  const isRefundCompleted = refundExecutionStatus === "completed";
  const hasRefundBlockedAccess =
    isRefundCompleted && Number(user?.is_paid) === 0;
  const hasRefundIssue =
    refundExecutionStatus === "failed" || refundExecutionStatus === "denied";
  const refundProcessedLabel = formatDateTimeBR(
    user?.access_hold_refund_processed_at
  );

  const showRenewalButton =
    isExpired && user?.renewal_notified === 0 && !isRefundCompleted;
  const canManageAccessHold =
    user?.is_paid === 1 || user?.isPaid === true;
  const hasDiscount = user?.discount_type && user.discount_type !== "none";
  const hasAccessHold = user?.access_hold_active;
  const hasTestOffer =
    Number(user?.subscription_days_override) > 0 ||
    Number(user?.is_test_user) === 1 ||
    user?.is_test_user === true;
  const discountTypeLower = String(user?.discount_type || "").toLowerCase();
  const phoneLabel = formatPhoneBR(user?.phone);
  const formattedFullName =
    formatPersonName(user?.full_name) || "Usuário sem nome";
  const discountLabel = hasDiscount
    ? getDiscountOfferText(user) || user.discount_type.replace("Desconto ", "R$")
    : "Nenhum";
  const discountColor = hasDiscount
    ? discountTypeLower.includes("199")
      ? theme.palette.success.main
      : discountTypeLower.includes("179")
      ? theme.palette.warning.main
      : discountTypeLower.includes("149")
      ? theme.palette.error.main
      : discountTypeLower.includes("valor_livre")
      ? theme.palette.info.main
      : theme.palette.text.secondary
    : theme.palette.text.secondary;

  const etapaFunilNormalizada =
    Number(
      normalizarEtapaVendasLegada(user?.etapa_vendas) ??
        user?.etapa_vendas ??
        1
    ) || 1;
  const salesSummary = user?.sales_event_summary || {};
  const salesScore = Number(user?.sales_score) || 0;
  const paymentViews = Number(salesSummary.total_payment_views) || 0;
  const whatsappClicks = Number(salesSummary.total_whatsapp_clicks) || 0;
  const hasPaymentStart =
    Boolean(salesSummary.has_payment_start) ||
    Number(salesSummary.total_pix_starts) > 0 ||
    Number(salesSummary.total_card_starts) > 0;
  const isPaidUser = Number(user?.is_paid) === 1 || user?.isPaid === true;
  const primarySalesAction =
    user?.next_sales_action?.title ||
    user?.acao_sugerida?.titulo ||
    (isPaidUser ? "Cliente ativo" : "Acompanhar lead");
  const leadTemperature = (() => {
    if (hasRefundBlockedAccess) {
      return {
        label: "Encerrado",
        color: "#ef4444",
        bg: "rgba(239, 68, 68, 0.14)",
        border: "rgba(239, 68, 68, 0.28)",
      };
    }

    if (isPaidUser) {
      return {
        label: "Cliente",
        color: "#22c55e",
        bg: "rgba(34, 197, 94, 0.14)",
        border: "rgba(34, 197, 94, 0.28)",
      };
    }

    const actionText = String(primarySalesAction).toLowerCase();
    const actionKey = String(user?.next_sales_action?.action_key || "").toLowerCase();

    if (
      hasPaymentStart ||
      salesScore >= 80 ||
      actionText.includes("urgente") ||
      actionKey.includes("urgent")
    ) {
      return {
        label: "Urgente",
        color: "#ef4444",
        bg: "rgba(239, 68, 68, 0.14)",
        border: "rgba(239, 68, 68, 0.28)",
      };
    }

    if (salesScore >= 60 || paymentViews >= 3 || whatsappClicks > 0) {
      return {
        label: "Quente",
        color: "#f97316",
        bg: "rgba(249, 115, 22, 0.15)",
        border: "rgba(249, 115, 22, 0.3)",
      };
    }

    if (salesScore >= 30 || paymentViews > 0) {
      return {
        label: "Morno",
        color: "#eab308",
        bg: "rgba(234, 179, 8, 0.14)",
        border: "rgba(234, 179, 8, 0.28)",
      };
    }

    return {
      label: "Frio",
      color: "#38bdf8",
      bg: "rgba(56, 189, 248, 0.14)",
      border: "rgba(56, 189, 248, 0.26)",
    };
  })();
  const leadReason = (() => {
    if (hasRefundBlockedAccess) return "Reembolso confirmado";

    if (isPaidUser) {
      if (user?.subscription_expires_at) {
        const expiresAt = new Date(user.subscription_expires_at);
        if (!Number.isNaN(expiresAt.getTime())) {
          const days = Math.ceil(
            (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
          );
          if (days >= 0) return `Renova em ${days} dias`;
        }
      }
      return "Assinatura ativa";
    }

    if (hasPaymentStart) return "Iniciou pagamento";
    if (paymentViews >= 3) return `Abriu pagamento ${paymentViews}x`;
    if (paymentViews > 0) return "Abriu pagamento";
    if (whatsappClicks > 0) return "Clicou no WhatsApp";
    if (salesSummary.last_seen_at) {
      return `Último acesso: ${formatShortAccessDate(salesSummary.last_seen_at)}`;
    }
    if (user?.texto_cadastro) return user.texto_cadastro;

    return "Sem interação recente";
  })();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 2,
          background: hasRefundBlockedAccess
            ? "linear-gradient(135deg, rgba(39,8,12,0.98) 0%, rgba(70,10,17,0.96) 52%, rgba(100,17,29,0.92) 100%)"
            : theme.palette.background.paper,
          boxShadow: hasRefundBlockedAccess
            ? "0 18px 36px rgba(24, 4, 8, 0.5), inset 0 0 0 1px rgba(248, 113, 113, 0.16)"
            : theme.shadows[2],
          borderRadius: theme.shape.borderRadius,
          marginBottom: 2,
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
          transition: "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
          border: hasRefundBlockedAccess
            ? "1px solid rgba(248, 113, 113, 0.24)"
            : "1px solid transparent",
          "&::before": hasRefundBlockedAccess
            ? {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, rgba(248,113,113,0.16) 0%, rgba(248,113,113,0) 22%), radial-gradient(circle at top right, rgba(127,29,29,0.32), transparent 42%)",
                pointerEvents: "none",
              }
            : undefined,
          "&:hover": {
            transform: "scale(1.01)",
            boxShadow: hasRefundBlockedAccess
              ? "0 22px 40px rgba(24, 4, 8, 0.56), inset 0 0 0 1px rgba(248, 113, 113, 0.24)"
              : theme.shadows[4],
          },
        }}
        onClick={handleToggleExpand}
      >
        {hasRefundBlockedAccess && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 48,
              px: 1.2,
              py: 0.55,
              borderRadius: "999px",
              bgcolor: "rgba(239, 68, 68, 0.18)",
              color: "#FFD3D3",
              border: "1px solid rgba(248, 113, 113, 0.32)",
              fontSize: "0.68rem",
              fontWeight: 900,
              letterSpacing: 1.1,
              textTransform: "uppercase",
              backdropFilter: "blur(8px)",
            }}
          >
            Reembolsado
          </Box>
        )}

        <Box sx={{ flexGrow: 1, pr: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontWeight: "bold",
                color: theme.palette.text.primary,
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              <FiberManualRecordIcon
                sx={{
                  fontSize: 20,
                  flexShrink: 0,
                  position: "relative",
                  top: "5px",
                  color:
  statusNormalizado === "reembolsado"
    ? theme.palette.error.main
    :
  statusNormalizado === "ativo"
    ? theme.palette.success.main
    : statusNormalizado === "pendente"
    ? theme.palette.warning.main
    : theme.palette.error.main,

                }}
              />

              {formattedFullName}
            </Typography>

            {user?.invalid_email === 1 && (
              <Tooltip title="E-mail inválido" arrow>
                <IconButton
                  size="small"
                  sx={{
                    color: blinkEmailInvalid
                      ? theme.palette.error.light
                      : theme.palette.error.dark,
                    "@keyframes blink": {
                      "50%": { opacity: 0.3 },
                    },
                    animation: blinkEmailInvalid ? "blink 1s infinite" : "none",
                  }}
                >
                  <EmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip
              title={
                user?.next_sales_action?.title ||
                "Abrir WhatsApp com rastreamento"
              }
              arrow
            >
              <IconButton
                size="small"
                onClick={handleTrackedAdminWhatsApp}
                sx={{ color: theme.palette.success.dark }}
              >
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {showDiscountWhatsApp && (
              <IconButton
                size="small"
                onClick={handleSendDiscountMessage}
                sx={{
                  color: blinkDiscount
                    ? theme.palette.success.light
                    : theme.palette.success.dark,
                  "@keyframes blink": {
                    "50%": { opacity: 0.3 },
                  },
                  animation: blinkDiscount ? "blink 1s infinite" : "none",
                }}
              >
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            )}

            {showRenewalButton && (
              <Tooltip title="Renovação pendente – enviar mensagem" arrow>
                <IconButton
                  size="small"
                  sx={{ color: theme.palette.warning.main, ml: 0.5 }}
                  onClick={handleSendRenewalMessage}
                  disabled={user.renewal_notified === 1}
                >
                  <AutorenewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {isMobile && !hasRefundBlockedAccess && (
            <Box
              sx={{
                mt: 0.9,
                px: 1,
                py: 0.85,
                borderRadius: 1,
                backgroundColor: leadTemperature.bg,
                border: `1px solid ${leadTemperature.border}`,
                borderLeft: `4px solid ${leadTemperature.color}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 0.45,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 900,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    color: leadTemperature.color,
                  }}
                >
                  {leadTemperature.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    lineHeight: 1.2,
                  }}
                >
                  {primarySalesAction}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: theme.palette.text.secondary,
                  lineHeight: 1.35,
                }}
              >
                {leadReason}
              </Typography>

              {user?.acao_sugerida &&
                etapaFunilNormalizada < FUNIL_ETAPA_ENCERRADA && (
                  <Box
                    onClick={handleAvancarEtapa}
                    sx={{
                      mt: 0.85,
                      px: 0.9,
                      py: 0.7,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      backgroundColor:
                        user.acao_sugerida?.colorHex
                          ? `${user.acao_sugerida.colorHex}1F`
                          : "rgba(59,130,246,0.14)",
                      border: `1px solid ${
                        user.acao_sugerida?.colorHex
                          ? `${user.acao_sugerida.colorHex}55`
                          : "rgba(59,130,246,0.24)"
                      }`,
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        fontSize: "0.74rem",
                        color: theme.palette.text.secondary,
                        lineHeight: 1.25,
                      }}
                    >
                      ⏱ {user.texto_cadastro || "lead em acompanhamento"}
                      <Box
                        component="span"
                        sx={{
                          color: user.acao_sugerida.colorHex,
                          fontWeight: 900,
                          textTransform: "uppercase",
                          ml: 0.5,
                        }}
                      >
                        · {user.acao_sugerida.titulo}
                      </Box>
                    </Typography>
                    {user.acao_sugerida.key && (
                      <ArrowForwardIcon
                        sx={{
                          color: user.acao_sugerida.colorHex,
                          fontSize: 20,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                )}
            </Box>
          )}
          {hasRefundBlockedAccess ? (
            <Box
              sx={{
                mt: 0.9,
                px: 1.25,
                py: 1,
                borderRadius: 2,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
                background:
                  "linear-gradient(135deg, rgba(53, 11, 18, 0.9) 0%, rgba(91, 17, 31, 0.94) 100%)",
                border: "1px solid rgba(248, 113, 113, 0.18)",
              }}
            >
              <TaskAltRoundedIcon
                sx={{ color: "#FFC2C2", fontSize: 18 }}
              />
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 900,
                  color: "#FFF1F1",
                  letterSpacing: 0.45,
                  textTransform: "uppercase",
                }}
              >
                Caso encerrado
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "rgba(255,235,235,0.88)",
                  fontWeight: 700,
                }}
              >
                Reembolso confirmado
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.76rem",
                  color: "rgba(255,218,218,0.78)",
                }}
              >
                • acesso bloqueado
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.76rem",
                  color: "rgba(255,218,218,0.72)",
                }}
              >
                • fora da régua comercial
              </Typography>
            </Box>
          ) : etapaFunilNormalizada >= FUNIL_ETAPA_ENCERRADA ? (
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: theme.palette.text.secondary,
                mt: 0.2,
                display: isMobile ? "none" : "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              ■ lead não convertido · último valor:{" "}
              <strong>
                {hasDiscount ? discountLabel : "sem desconto"}
              </strong>
            </Typography>
          ) : (
            <>
              {user?.acao_sugerida && !isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                    px: 1,
                    py: 0.6,
                    borderRadius: 1,
                    backgroundColor: `${user.acao_sugerida.colorHex}22`,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                  >
                    ⏱ {user.texto_cadastro}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      color: user.acao_sugerida.colorHex,
                    }}
                  >
                    · {user.acao_sugerida.titulo}
                  </Typography>

                  {user.acao_sugerida.key && (
                    <IconButton
                      size="small"
                      sx={{
                        color: user.acao_sugerida.colorHex,
                        p: 0.3,
                      }}
                      title="Registrar etapa e abrir WhatsApp"
                      onClick={handleAvancarEtapa}
                    >
                      <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              {!isMobile && (Number(user?.sales_score) > 0 || user?.next_sales_action) ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  {Number.isFinite(Number(user?.sales_score)) && (
                    <Typography
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      Score: <strong>{user.sales_score}</strong>
                    </Typography>
                  )}
                  {user?.next_sales_action?.title && (
                    <Typography
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      Próxima ação:{" "}
                      <strong>{user.next_sales_action.title}</strong>
                    </Typography>
                  )}
                  {user?.sales_event_summary && (
                    <>
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                      >
                        Pagamentos vistos:{" "}
                        <strong>
                          {user.sales_event_summary.total_payment_views || 0}
                        </strong>
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                      >
                        Último acesso:{" "}
                        <strong>
                          {formatShortAccessDate(
                            user.sales_event_summary.last_seen_at
                          )}
                        </strong>
                      </Typography>
                    </>
                  )}
                </Box>
              ) : null}
            </>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
            }}
          >
            {user?.tags?.length > 0 &&
              user.tags.map((tag) => (
                <Box
                  key={tag.id}
                  sx={{
                    backgroundColor: tag.color,
                    color: "#fff",
                    px: 1,
                    py: 0.2,
                    borderRadius: "12px",
                    fontSize: "0.75rem",
                    fontWeight: "medium",
                  }}
                >
                  {tag.name}
                </Box>
              ))}
            {hasDiscount && (
              <LocalOfferIcon
                sx={{
                  color: discountColor,
                  fontSize: 18,
                }}
              />
            )}
            {hasTestOffer && (
              <Box
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: "12px",
                  backgroundColor: "rgba(66,165,245,0.16)",
                  color: "#42a5f5",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: 0.4,
                }}
              >
                TESTE
              </Box>
            )}
            {hasAccessHold && (
              <Box
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: "12px",
                  backgroundColor: hasRefundBlockedAccess
                    ? "rgba(239,68,68,0.18)"
                    : isRefundCompleted
                    ? "rgba(239,68,68,0.18)"
                    : "rgba(244,67,54,0.14)",
                  color: hasRefundBlockedAccess
                    ? "#FFC7C7"
                    : isRefundCompleted
                    ? "#FFC7C7"
                    : theme.palette.error.main,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                }}
              >
                {hasRefundBlockedAccess
                  ? "Reembolsado • acesso bloqueado"
                  : isRefundCompleted
                  ? "Reembolso concluído"
                  : hasRefundIssue
                  ? "Reembolso com falha"
                  : "Análise suspeita ativa"}
              </Box>
            )}
          </Box>
        </Box>

        <Tooltip title="Mais opções" arrow placement="top-end">
          <IconButton
            ref={menuButtonRef}
            onClick={handleToggleMenu}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>

        {menuOpen && (
          <Portal>
            <ClickAwayListener onClickAway={handleCloseMenu}>
              <Box
                sx={{
                  position: "absolute",
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`,
                  transform: "translate(-50%, 0)",
                  width: 104,
                  height: 104,
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  zIndex: 99999,
                  animation: "expandAndFade 0.5s ease-out forwards",
                  "@keyframes expandAndFade": {
                    "0%": {
                      opacity: 0,
                      transform: "translate(-50%, 0) scale(0)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translate(-50%, 0) scale(1)",
                    },
                  },
                }}
              >
                {[
                  {
                    icon: <CurrencyExchangeIcon />,
                    color: theme.palette.primary.main,
                    onClick: (e) => {
                      e.stopPropagation();
                      if (typeof setActiveMenu === "function") {
                        setActiveMenu(null);
                        setOpenEditDialog(true);
                      }
                    },
                  },
                  canManageAccessHold
                    ? {
                        icon: <UndoRoundedIcon />,
                        color: theme.palette.error.light,
                        onClick: handleRefundLatestPayment,
                      }
                    : null,
                  {
                    icon: <ManageAccountsIcon />,
                    color: theme.palette.info.main,
                    onClick: (e) => {
                      e.stopPropagation();
                      if (typeof setActiveMenu === "function") {
                        setActiveMenu(null);
                        setOpenEditUserModal(true);
                      }
                    },
                  },
                  canManageAccessHold
                    ? {
                        icon: <WarningAmberRoundedIcon />,
                        color: hasAccessHold
                          ? theme.palette.error.main
                          : theme.palette.warning.main,
                        onClick: (e) => {
                          e.stopPropagation();
                          if (typeof setActiveMenu === "function") {
                            setActiveMenu(null);
                            setOpenAccessHoldModal(true);
                          }
                        },
                      }
                    : null,
                  {
                    icon: <DeleteIcon />,
                    color: theme.palette.error.main,
                    onClick: (e) => {
                      e.stopPropagation();
                      if (typeof setActiveMenu === "function") {
                        user?.id && onDelete(user.id);
                        setActiveMenu(null);
                      }
                    },
                  },
                  {
                    icon: <LocalOfferIcon />,
                    color: theme.palette.secondary.main,
                    onClick: (e) => {
                      e.stopPropagation();
                      if (typeof setActiveMenu === "function") {
                        setActiveMenu(null);
                        setOpenTagModal(true);
                      }
                    },
                  },
                ].filter(Boolean).map((item, index, arr) => {
                  const { angles, radius } = getRadialMenuLayout(arr.length);
                  const angle = angles[index];

                  return (
                    <IconButton
                      key={index}
                      onClick={item.onClick}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: `rotate(${angle}deg) translate(${radius}px) rotate(-${angle}deg)`,
                        color: item.color,
                        backgroundColor: theme.palette.background.paper,
                        width: 38,
                        height: 38,
                        boxShadow: "0 8px 18px rgba(0,0,0,0.22)",
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      {item.icon}
                    </IconButton>
                  );
                })}
              </Box>
            </ClickAwayListener>
          </Portal>
        )}
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
        sx={{
          p: 2,
          background: hasRefundBlockedAccess
            ? "linear-gradient(180deg, rgba(31,8,14,0.98) 0%, rgba(44,10,18,0.98) 100%)"
            : theme.palette.background.default,
            borderRadius: theme.shape.borderRadius,
            marginBottom: 2,
            ml: 2,
            mr: 2,
            zIndex: 1,
            border: hasRefundBlockedAccess
              ? "1px solid rgba(248, 113, 113, 0.16)"
              : "1px solid transparent",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {hasRefundBlockedAccess && (
              <Box
                sx={{
                  mb: 0.75,
                  px: 1.35,
                  py: 1.1,
                  borderRadius: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                  background:
                    "linear-gradient(135deg, rgba(56, 10, 18, 0.94) 0%, rgba(101, 18, 35, 0.9) 100%)",
                  border: "1px solid rgba(248, 113, 113, 0.18)",
                }}
              >
                <TaskAltRoundedIcon
                  sx={{ color: "#FFD0D0", fontSize: 20 }}
                />
                <Typography
                  sx={{
                    color: "#FFF2F2",
                    fontSize: "0.9rem",
                    fontWeight: 900,
                    letterSpacing: 0.35,
                    textTransform: "uppercase",
                  }}
                >
                  Reembolso finalizado
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    py: 0.3,
                    borderRadius: "999px",
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#FFE0E0",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                  }}
                >
                  {refundProcessedLabel}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.3,
                    borderRadius: "999px",
                    bgcolor: "rgba(239, 68, 68, 0.22)",
                    color: "#FFE0E0",
                    fontSize: "0.72rem",
                    fontWeight: 800,
                  }}
                >
                  <BlockRoundedIcon sx={{ fontSize: 15 }} />
                  Acesso bloqueado
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon
                sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
              />
              <Typography
                sx={{ color: theme.palette.text.primary, fontSize: "0.9rem" }}
              >
                {user?.email || "N/A"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WhatsAppIcon
                sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
              />
              <Typography
                sx={{ color: theme.palette.text.primary, fontSize: "0.9rem" }}
              >
                {phoneLabel}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventIcon
                sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
              />
              <Typography
                sx={{ color: theme.palette.text.primary, fontSize: "0.9rem" }}
              >
                Criado em:{" "}
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : "N/A"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventIcon
                sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
              />
              <Typography
                sx={{ color: theme.palette.text.primary, fontSize: "0.9rem" }}
              >
                Expira em:{" "}
                {user?.subscription_expires_at
                  ? new Date(user.subscription_expires_at).toLocaleDateString()
                  : "N/A"}
              </Typography>
            </Box>

            {hasDiscount && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CurrencyExchangeIcon
                  sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
                />
                <Typography
                  sx={{
                    color: discountColor,
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                  }}
                >
                  Desconto: {discountLabel}
                </Typography>
              </Box>
            )}

            {hasAccessHold && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FiberManualRecordIcon
                    sx={{
                      color: hasRefundBlockedAccess
                        ? theme.palette.info.main
                        : isRefundCompleted
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                      fontSize: 14,
                    }}
                  />
                  <Typography
                    sx={{
                      color: hasRefundBlockedAccess
                        ? theme.palette.info.main
                        : isRefundCompleted
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                      fontSize: "0.9rem",
                      fontWeight: 700,
                    }}
                  >
                    {hasRefundBlockedAccess
                      ? `Reembolso confirmado em ${formatDateTimeBR(
                          user?.access_hold_refund_processed_at
                        )}`
                      : isRefundCompleted
                      ? `Reembolso concluído em ${formatDateTimeBR(
                          user?.access_hold_refund_processed_at
                        )}`
                      : "Trava ativa desde "}
                    {!isRefundCompleted
                      ? formatDateTimeBR(user?.access_hold_created_at)
                      : ""}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.88rem",
                    lineHeight: 1.6,
                  }}
                >
                  {hasRefundBlockedAccess
                    ? "O estorno foi confirmado e o acesso do usuário foi bloqueado automaticamente."
                    : isRefundCompleted
                    ? "O estorno foi confirmado pelo Asaas."
                    : hasRefundIssue
                    ? user?.access_hold_refund_last_error ||
                      "O estorno automático falhou e precisa de revisão."
                    : user?.access_hold_reason ||
                      "Conta em análise por atividade suspeita."}
                </Typography>

                {(user?.access_hold_refund_payment_id || user?.access_hold_refund_processed_at) && (
                  <Typography
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.82rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {user?.access_hold_refund_payment_id
                      ? `Pagamento: ${user.access_hold_refund_payment_id}. `
                      : ""}
                    {user?.access_hold_refund_processed_at
                      ? `Última atualização: ${formatDateTimeBR(
                          user.access_hold_refund_processed_at
                        )}.`
                      : ""}
                  </Typography>
                )}

                {hasRefundBlockedAccess && (
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "0.8rem",
                      lineHeight: 1.7,
                      mt: 0.2,
                    }}
                  >
                    Esse usuário já saiu do fluxo comercial. Se houver contato futuro, o contexto é suporte ou conferência do reembolso.
                  </Typography>
                )}
              </>
            )}

            <Divider sx={{ my: 1 }} />
          </Box>
        </Box>
      </Collapse>

      <EditDiscountModal
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        user={user}
        onConfirm={async (newDiscount) => {
          try {
            console.log("🟡 Enviando desconto para o backend:", newDiscount);

            const response = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/users/${user?.id}/discount`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
body: JSON.stringify(newDiscount),
              }
            );

            console.log("🔵 Status da resposta:", response.status);

            let data = null;
            try {
              data = await response.json();
              console.log("🟢 JSON recebido:", data);
            } catch (e) {
              console.warn("⚠️ Não foi possível converter para JSON:", e);
            }

            if (!response.ok) {
              console.error("❌ Backend retornou erro:", data);
              throw new Error("Erro ao aplicar o desconto");
            }

onUpdateDiscount(user?.id, {
  discount_type: data?.discount_type || newDiscount.discount_type,
  custom_value: data?.custom_value ?? newDiscount.custom_value ?? null,
  subscription_days_override:
    data?.subscription_days_override ??
    newDiscount.subscription_days_override ??
    null,
});

          } catch (error) {
            console.error("❌ Erro ao aplicar desconto:", error);
          }
        }}
      />

      <EditUserModal
        open={openEditUserModal}
        onClose={() => setOpenEditUserModal(false)}
        user={user}
      />

      <TagManagerModal
        open={openTagModal}
        onClose={() => setOpenTagModal(false)}
        user={user}
        onUpdateTags={onUpdateTags}
      />

      <AccessHoldModal
        open={openAccessHoldModal}
        onClose={() => setOpenAccessHoldModal(false)}
        user={user}
        onConfirm={async (payload) => {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/users/${user.id}/access-hold`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );

          const data = await response.json();
          if (!response.ok) {
            throw new Error(
              data?.message || "Erro ao atualizar trava de acesso."
            );
          }

          const nextHold = data?.accessHold || null;
          if (typeof onUpdateUser === "function") {
            onUpdateUser({
              ...user,
              access_hold_active: nextHold ? 1 : 0,
              access_hold_type: nextHold?.holdType || null,
              access_hold_reason: nextHold?.reason || null,
              access_hold_created_at: nextHold?.createdAt || null,
              access_hold_refund_requested_at:
                nextHold?.summary?.refundRequestedAt || null,
              access_hold_refund_due_days:
                payload?.refund_due_days ?? null,
            });
          }
        }}
      />
    </>
  );
};

export default UserRow;
